import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  getGuardActiveVisits,
  getGuardUpcomingVisitors,
  checkoutVisitor,
  acknowledgeVisitFlag,
} from '@/services/api.client';
import {
  writeGateQueueCache,
  readGateQueueCache,
  isCacheStale,
  type CachedGateQueue,
} from '@/services/guard-offline.service';
import { useVisibilityPolling } from '@/hooks/useVisibilityPolling';
import type { CheckedInVisitor, UpcomingVisitor } from '@/types';

const POLL_INTERVAL_MS = 30_000;

export interface GateQueueData {
  checkedIn: CheckedInVisitor[];
  upcoming: UpcomingVisitor[];
  /** Walk-in visitors currently checked in on-site (already approved — not pending). */
  walkInsOnSite: number;
  isLoading: boolean;
  isOffline: boolean;
  isStale: boolean;
  lastUpdated: Date | null;
  error: unknown;
  refetch: () => void;
  checkOut: (visitId: string) => void;
  isCheckingOut: boolean;
  setAside: (visitId: string) => void;
  isSettingAside: boolean;
}

/**
 * Polls gate queue data every 30s (pauses when tab is hidden).
 * Falls back to IndexedDB cache when offline.
 */
export function useGateQueue(gateId: string): GateQueueData {
  const queryClient = useQueryClient();
  const refetchInterval = useVisibilityPolling(POLL_INTERVAL_MS);
  const [offlineCache, setOfflineCache] = useState<CachedGateQueue | null>(null);

  const activeVisitsQuery = useQuery({
    queryKey: ['guard', 'active-visits', gateId],
    queryFn: () => getGuardActiveVisits(gateId || undefined),
    refetchInterval,
    retry: 1,
  });

  const upcomingQuery = useQuery({
    queryKey: ['guard', 'upcoming-visitors', gateId],
    queryFn: () => getGuardUpcomingVisitors(gateId || undefined),
    refetchInterval,
    retry: 1,
  });

  const checkoutMutation = useMutation({
    mutationFn: checkoutVisitor,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['guard', 'active-visits'] });
      void queryClient.invalidateQueries({ queryKey: ['guard', 'upcoming-visitors'] });
    },
  });

  const setAsideMutation = useMutation({
    mutationFn: acknowledgeVisitFlag,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['guard', 'active-visits'] });
    },
  });

  const isOffline =
    activeVisitsQuery.error != null &&
    !activeVisitsQuery.isFetching &&
    activeVisitsQuery.data == null;

  // Write to IndexedDB cache on successful fetch
  useEffect(() => {
    if (activeVisitsQuery.data && gateId) {
      const checkedIn = activeVisitsQuery.data;
      const upcoming = upcomingQuery.data ?? [];
      void writeGateQueueCache({
        gateId,
        checkedIn,
        upcoming,
        walkInsOnSite: checkedIn.filter((v) => v.invitationType === 'walk_in').length,
        cachedAt: Date.now(),
      });
    }
  }, [activeVisitsQuery.data, upcomingQuery.data, gateId]);

  // Read from IndexedDB cache when offline
  useEffect(() => {
    if (isOffline && gateId) {
      void readGateQueueCache(gateId).then((cached) => {
        if (cached) setOfflineCache(cached);
      });
    } else {
      setOfflineCache(null);
    }
  }, [isOffline, gateId]);

  const checkedIn = activeVisitsQuery.data ?? offlineCache?.checkedIn ?? [];
  const upcoming = upcomingQuery.data ?? offlineCache?.upcoming ?? [];
  const walkInsOnSite = activeVisitsQuery.data
    ? checkedIn.filter((v) => v.invitationType === 'walk_in').length
    : (offlineCache?.walkInsOnSite ?? 0);

  const isStale = offlineCache != null && isCacheStale(offlineCache);

  const lastUpdated =
    activeVisitsQuery.dataUpdatedAt > 0
      ? new Date(activeVisitsQuery.dataUpdatedAt)
      : offlineCache
        ? new Date(offlineCache.cachedAt)
        : null;

  return {
    checkedIn,
    upcoming,
    walkInsOnSite,
    isLoading: activeVisitsQuery.isLoading || upcomingQuery.isLoading,
    isOffline,
    isStale,
    lastUpdated,
    error: activeVisitsQuery.error ?? upcomingQuery.error,
    refetch: () => {
      void activeVisitsQuery.refetch();
      void upcomingQuery.refetch();
    },
    checkOut: (visitId: string) => checkoutMutation.mutate(visitId),
    isCheckingOut: checkoutMutation.isPending,
    setAside: (visitId: string) => setAsideMutation.mutate(visitId),
    isSettingAside: setAsideMutation.isPending,
  };
}
